import React, { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Layout from '../../components/Layout';
import { useToast } from '../../contexts/ToastContext';
import {
  adminBillingApi,
  AdminProforma,
  ListProformasParams,
  ImportInvoicesResponse,
} from '../../services/adminBillingApi';

/**
 * Format date to Polish format (DD.MM.YYYY)
 */
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('pl-PL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

/**
 * Format money amount with currency
 */
const formatMoney = (amount: number, currency: string): string => {
  return new Intl.NumberFormat('pl-PL', {
    style: 'currency',
    currency: currency,
  }).format(amount);
};

/**
 * Proforma Status Badge Component
 */
const StatusBadge: React.FC<{
  status: 'issued' | 'paid' | 'overdue';
  dueDate?: string;
}> = ({ status, dueDate }) => {
  const config: Record<string, { label: string; style: string }> = {
    paid: { label: 'Opłacona', style: 'bg-green-100 text-green-800' },
    issued: { label: 'Wystawiona', style: 'bg-yellow-100 text-yellow-800' },
    overdue: { label: 'Przeterminowana', style: 'bg-red-100 text-red-800' },
  };

  const { label, style } = config[status];

  return (
    <div className="flex flex-col">
      <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${style}`}>
        {label}
      </span>
      {status === 'issued' && dueDate && (
        <span className="text-xs text-neutral-500 mt-1">
          Termin: {formatDate(dueDate)}
        </span>
      )}
    </div>
  );
};

/**
 * Mark As Paid Modal Component
 */
const MarkPaidModal: React.FC<{
  proforma: AdminProforma;
  isLoading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ proforma, isLoading, onConfirm, onCancel }) => {
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4 text-center">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50 transition-opacity"
          onClick={onCancel}
        />

        {/* Modal Panel */}
        <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
          <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-green-100 sm:mx-0 sm:h-10 sm:w-10">
                <svg
                  className="h-6 w-6 text-green-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z"
                  />
                </svg>
              </div>
              <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                <h3 className="text-lg font-semibold leading-6 text-neutral-900">
                  Oznacz jako opłaconą
                </h3>
                <div className="mt-4 space-y-3">
                  <div className="text-sm text-neutral-700">
                    <strong>Proforma:</strong> {proforma.proformaNumber}
                  </div>
                  <div className="text-sm text-neutral-700">
                    <strong>Tenant:</strong> {proforma.tenant.name}
                  </div>
                  <div className="text-sm text-neutral-700">
                    <strong>Kwota:</strong>{' '}
                    {formatMoney(proforma.amountGross, proforma.currency)} brutto
                  </div>

                  <div className="mt-4 p-3 bg-primary-50 rounded-md">
                    <p className="text-sm text-primary-800">
                      Po potwierdzeniu system:
                    </p>
                    <ul className="mt-2 text-sm text-primary-800 list-disc list-inside space-y-1">
                      <li>Zmieni status proformy na "opłacona"</li>
                      <li>Przedłuży subskrypcję o okres rozliczeniowy</li>
                      <li>Wystawi fakturę VAT</li>
                      <li>Wyśle fakturę emailem do klienta</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-neutral-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6 gap-2">
            <button
              type="button"
              onClick={onConfirm}
              disabled={isLoading}
              className="inline-flex w-full justify-center rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500 sm:ml-3 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Przetwarzanie...
                </>
              ) : (
                'Potwierdź płatność'
              )}
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={isLoading}
              className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 hover:bg-neutral-50 sm:mt-0 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Anuluj
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Import Invoices Modal Component
 */
const ImportModal: React.FC<{
  isLoading: boolean;
  result: ImportInvoicesResponse | null;
  onImport: (dryRun: boolean) => void;
  onClose: () => void;
}> = ({ isLoading, result, onImport, onClose }) => {
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4 text-center">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50 transition-opacity"
          onClick={!isLoading ? onClose : undefined}
        />

        {/* Modal Panel */}
        <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
          <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 sm:mx-0 sm:h-10 sm:w-10">
                <svg
                  className="h-6 w-6 text-primary-700"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"
                  />
                </svg>
              </div>
              <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left flex-1">
                <h3 className="text-lg font-semibold leading-6 text-neutral-900">
                  Import faktur z Fakturownia
                </h3>

                {!result ? (
                  <div className="mt-4 space-y-3">
                    <p className="text-sm text-neutral-700">
                      Importuje wszystkie faktury VAT i proforma z Fakturownia.
                      Dopasowuje faktury do tenantów po NIP.
                    </p>
                    <div className="p-3 bg-yellow-50 rounded-md">
                      <p className="text-sm text-yellow-800">
                        <strong>Uwaga:</strong> Import jest idempotentny - można
                        uruchomić wielokrotnie bez ryzyka duplikacji.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="mt-4 space-y-3">
                    <div className="p-3 bg-green-50 rounded-md">
                      <p className="text-sm font-medium text-green-800">
                        Import zakończony ({result.duration})
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="font-medium text-neutral-700">
                          Zaimportowano:
                        </p>
                        <ul className="mt-1 text-neutral-600 list-disc list-inside">
                          <li>VAT: {result.imported.vat}</li>
                          <li>Proforma: {result.imported.proforma}</li>
                        </ul>
                      </div>
                      <div>
                        <p className="font-medium text-neutral-700">Pominięto:</p>
                        <ul className="mt-1 text-neutral-600 list-disc list-inside">
                          <li>Już istnieje: {result.skipped.alreadyExists}</li>
                          <li>Brak tenanta: {result.skipped.noMatchingTenant}</li>
                          <li>Brak subskrypcji: {result.skipped.noSubscription}</li>
                        </ul>
                      </div>
                    </div>

                    {result.errors.length > 0 && (
                      <div className="p-3 bg-red-50 rounded-md">
                        <p className="text-sm font-medium text-red-800">
                          Błędy ({result.errors.length}):
                        </p>
                        <ul className="mt-1 text-xs text-red-700 max-h-32 overflow-y-auto">
                          {result.errors.slice(0, 5).map((err, i) => (
                            <li key={i}>
                              {err.invoiceNumber}: {err.error}
                            </li>
                          ))}
                          {result.errors.length > 5 && (
                            <li>...i {result.errors.length - 5} więcej</li>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="bg-neutral-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6 gap-2">
            {!result ? (
              <>
                <button
                  type="button"
                  onClick={() => onImport(false)}
                  disabled={isLoading}
                  className="inline-flex w-full justify-center rounded-md bg-primary-700 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-800 sm:ml-3 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Importowanie...
                    </>
                  ) : (
                    'Importuj'
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => onImport(true)}
                  disabled={isLoading}
                  className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 hover:bg-neutral-50 sm:mt-0 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Dry run (test)
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={onClose}
                className="inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 hover:bg-neutral-50 sm:w-auto"
              >
                Zamknij
              </button>
            )}
            {!result && (
              <button
                type="button"
                onClick={onClose}
                disabled={isLoading}
                className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 hover:bg-neutral-50 sm:mt-0 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Anuluj
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Admin Proforma Invoices Page
 *
 * Cross-tenant proforma management for platform administrators.
 * Used for quick payment processing after bank transfers.
 *
 * Features:
 * - Search by proforma number
 * - Filter by status
 * - Pagination
 * - Mark as paid with modal confirmation
 */
const AdminProformaInvoices: React.FC = () => {
  const { addToast } = useToast();
  const queryClient = useQueryClient();

  // State
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<
    'issued' | 'paid' | 'overdue' | ''
  >('');
  const [page, setPage] = useState(1);
  const [selectedProforma, setSelectedProforma] = useState<AdminProforma | null>(
    null,
  );
  const [showImportModal, setShowImportModal] = useState(false);
  const [importResult, setImportResult] = useState<ImportInvoicesResponse | null>(
    null,
  );

  // Debounce search input
  const debounceTimerRef = React.useRef<NodeJS.Timeout | null>(null);
  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1); // Reset to first page on search
    }, 300);
  }, []);

  // Build query params
  const queryParams: ListProformasParams = {
    page,
    limit: 20,
    ...(debouncedSearch && { search: debouncedSearch }),
    ...(statusFilter && { status: statusFilter }),
  };

  // Fetch proformas
  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['admin-proformas', queryParams],
    queryFn: () => adminBillingApi.getProformas(queryParams),
  });

  // Mark as paid mutation
  const markAsPaidMutation = useMutation({
    mutationFn: (proformaId: string) => adminBillingApi.markAsPaid(proformaId),
    onSuccess: (result) => {
      setSelectedProforma(null);
      queryClient.invalidateQueries({ queryKey: ['admin-proformas'] });

      if (result.message === 'Proforma already marked as paid') {
        addToast({
          message: 'Proforma była już oznaczona jako opłacona.',
          type: 'info',
        });
      } else {
        addToast({
          message: `Płatność zaksięgowana. Faktura VAT: ${result.invoiceNumber || 'wystawiona'}`,
          type: 'success',
        });
      }
    },
    onError: (error: Error) => {
      addToast({ message: error.message, type: 'error' });
    },
  });

  // Import invoices mutation
  const importMutation = useMutation({
    mutationFn: (dryRun: boolean) => adminBillingApi.importInvoices(dryRun),
    onSuccess: (result) => {
      setImportResult(result);
      queryClient.invalidateQueries({ queryKey: ['admin-proformas'] });

      const total = result.imported.vat + result.imported.proforma;
      if (total > 0) {
        addToast({
          message: `Zaimportowano ${total} faktur (VAT: ${result.imported.vat}, proforma: ${result.imported.proforma})`,
          type: 'success',
        });
      } else {
        addToast({
          message: 'Brak nowych faktur do zaimportowania.',
          type: 'info',
        });
      }
    },
    onError: (error: Error) => {
      addToast({ message: error.message, type: 'error' });
    },
  });

  // Handle import modal close
  const handleCloseImportModal = () => {
    setShowImportModal(false);
    setImportResult(null);
  };

  // Handle status filter change
  const handleStatusChange = (value: string) => {
    setStatusFilter(value as 'issued' | 'paid' | 'overdue' | '');
    setPage(1);
  };

  // Calculate total pages
  const totalPages = data ? Math.ceil(data.total / data.limit) : 0;

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">
              Proformy - Zarządzanie
            </h1>
            <p className="text-neutral-600 mt-1">
              Lista proform ze wszystkich tenantów. Oznacz jako opłacone po
              otrzymaniu przelewu.
            </p>
          </div>
          <button
            onClick={() => setShowImportModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-700 hover:bg-primary-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <svg
              className="h-5 w-5 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
              />
            </svg>
            Import z Fakturownia
          </button>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <label htmlFor="search" className="sr-only">
              Szukaj po numerze proformy
            </label>
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <svg
                  className="h-5 w-5 text-neutral-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <input
                type="text"
                id="search"
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder="Szukaj po numerze proformy (np. FPro/2026/01/001)"
                className="block w-full rounded-md border-0 py-2 pl-10 pr-3 text-neutral-900 ring-1 ring-inset ring-neutral-300 placeholder:text-neutral-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
              />
            </div>
          </div>

          <div className="sm:w-48">
            <label htmlFor="status" className="sr-only">
              Filtruj po statusie
            </label>
            <select
              id="status"
              value={statusFilter}
              onChange={(e) => handleStatusChange(e.target.value)}
              className="block w-full rounded-md border-0 py-2 pl-3 pr-10 text-neutral-900 ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-primary-600 sm:text-sm sm:leading-6"
            >
              <option value="">Wszystkie statusy</option>
              <option value="issued">Wystawione</option>
              <option value="paid">Opłacone</option>
              <option value="overdue">Przeterminowane</option>
            </select>
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="bg-white shadow-sm rounded-lg border border-neutral-200 p-8">
            <div className="flex items-center justify-center">
              <svg
                className="animate-spin h-8 w-8 text-primary-600"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <span className="ml-3 text-neutral-600">Ładowanie proform...</span>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex items-start gap-3">
              <svg
                className="h-5 w-5 text-red-400 flex-shrink-0"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
              <div className="flex-1">
                <h3 className="text-sm font-medium text-red-800">
                  Nie udało się załadować proform
                </h3>
                <p className="text-sm text-red-700 mt-1">
                  {(error as Error).message}
                </p>
                <button
                  onClick={() => refetch()}
                  className="mt-3 text-sm font-medium text-red-700 hover:text-red-800 underline"
                >
                  Spróbuj ponownie
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Empty State */}
        {data && data.items.length === 0 && !isLoading && !error && (
          <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-8 text-center">
            <svg
              className="mx-auto h-12 w-12 text-neutral-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <h3 className="mt-2 text-sm font-semibold text-neutral-900">
              Brak proform
            </h3>
            <p className="mt-1 text-sm text-neutral-500">
              {debouncedSearch || statusFilter
                ? 'Nie znaleziono proform pasujących do filtrów.'
                : 'Nie ma jeszcze żadnych proform w systemie.'}
            </p>
          </div>
        )}

        {/* Proformas Table */}
        {data && data.items.length > 0 && !isLoading && !error && (
          <>
            <div className="bg-white shadow-sm rounded-lg border border-neutral-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full" aria-label="Lista proform">
                  <thead className="bg-neutral-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                        Numer
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                        Tenant
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                        Kwota
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                        Akcje
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-200">
                    {data.items.map((proforma) => (
                      <tr key={proforma.id} className="hover:bg-neutral-50">
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-neutral-900">
                            {proforma.proformaNumber}
                          </div>
                          <div className="text-xs text-neutral-500">
                            {formatDate(proforma.issueDate)}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-neutral-900">
                            {proforma.tenant.name}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm font-mono text-neutral-900">
                          {formatMoney(proforma.amountGross, proforma.currency)}
                        </td>
                        <td className="px-6 py-4">
                          <StatusBadge
                            status={proforma.status}
                            dueDate={proforma.dueDate}
                          />
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            {proforma.status !== 'paid' && (
                              <button
                                onClick={() => setSelectedProforma(proforma)}
                                className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-green-700 bg-green-100 hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                                title="Oznacz jako opłaconą"
                              >
                                <svg
                                  className="h-4 w-4 mr-1"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                  />
                                </svg>
                                Opłacona
                              </button>
                            )}
                            <a
                              href={proforma.pdfUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center px-2.5 py-1.5 border border-neutral-300 text-xs font-medium rounded text-neutral-700 bg-white hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                              title="Pobierz PDF"
                            >
                              <svg
                                className="h-4 w-4 mr-1"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                />
                              </svg>
                              PDF
                            </a>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-neutral-200 bg-white px-4 py-3 sm:px-6 rounded-lg shadow-sm">
                <div className="flex flex-1 justify-between sm:hidden">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="relative inline-flex items-center rounded-md border border-neutral-300 bg-white px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Poprzednia
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="relative ml-3 inline-flex items-center rounded-md border border-neutral-300 bg-white px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Następna
                  </button>
                </div>
                <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-neutral-700">
                      Strona <span className="font-medium">{page}</span> z{' '}
                      <span className="font-medium">{totalPages}</span> (
                      <span className="font-medium">{data.total}</span> proform)
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="relative inline-flex items-center rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Poprzednia
                    </button>
                    <button
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      className="relative inline-flex items-center rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Następna
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Mark Paid Modal */}
        {selectedProforma && (
          <MarkPaidModal
            proforma={selectedProforma}
            isLoading={markAsPaidMutation.isPending}
            onConfirm={() => markAsPaidMutation.mutate(selectedProforma.id)}
            onCancel={() => setSelectedProforma(null)}
          />
        )}

        {/* Import Invoices Modal */}
        {showImportModal && (
          <ImportModal
            isLoading={importMutation.isPending}
            result={importResult}
            onImport={(dryRun) => importMutation.mutate(dryRun)}
            onClose={handleCloseImportModal}
          />
        )}
      </div>
    </Layout>
  );
};

export default AdminProformaInvoices;
